import { useMutation } from '@apollo/client'
import { PosthogEvent, ROUTES } from '@app/constants'
import { DownvoteDocument, TypesOfContent, UpvoteDocument, useTipDetailedLazyQuery } from '@app/graphql'
import { UpvoteStatus } from '@app/types'
import { FC, memo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Button from '../Button'
import { Icon, IconColor, IconTypes } from '../Icon'
import { useTranslation } from 'react-i18next'
import {  useSnapshot } from 'valtio'
import {
  CurrentUserState,
  guestMode,
  PrivateChannelState,
  PublicChannelState,
  showSignupPromptModal,
} from '@app/storage'
import { captureAnalytics } from '@app/helpers/misc'
import { useSaveTip } from '@app/hooks/useSaveTip'
import { themeManager } from '@app/helpers/theme'

interface FeedPostActionsProps {
  id: string;
  upvotes: number | null;
  downvotes: number | null;
  contentType: TypesOfContent;
  commentsAllowed: boolean;
  commentsCount: number | null;
  status?: UpvoteStatus;
  stake?: number;
  odds?: number;
  profit?: string;
  saved?: boolean;
  multiple?: boolean;
  createdAt?: Date;
  sportIds?: string[];
  hideSave?: boolean;
}

const FeedPostActions: FC<FeedPostActionsProps> = ({
  id,
  upvotes,
  downvotes,
  contentType,
  commentsAllowed,
  commentsCount,
  status,
  saved = false,
  multiple,
  createdAt,
  sportIds,
  hideSave,
}) => {
  const { t } = useTranslation()
  const isGuest = guestMode()
  const isTipContent = contentType === TypesOfContent.Tip || contentType === TypesOfContent.Multitip
  const { savedStatus, onSaveClick } = useSaveTip({ id, type: contentType, saved, multiple, createdAt, sportIds })
  const initialUpvotesCount = Number.isFinite(upvotes) && Number.isFinite(downvotes) ? upvotes - downvotes : null
  const [ getSingleTip ] =
    useTipDetailedLazyQuery({ fetchPolicy: 'network-only' })
  const [ upvotesCount, setUpvotesCount ] = useState<number | null>(initialUpvotesCount)
  const [ upvoteStatus, setUpvoteStatus ] = useState<UpvoteStatus>(status || UpvoteStatus.none)
  const [ upvote ] = useMutation(UpvoteDocument, { ignoreResults: true })
  const [ downvote ] = useMutation(DownvoteDocument, { ignoreResults: true })
  const { isPublic } = useSnapshot(PublicChannelState)

  const navigate = useNavigate()

  useEffect(() => {
    setUpvotesCount(Number.isFinite(upvotes) && Number.isFinite(downvotes) ? upvotes - downvotes : null)
  }, [ upvotes, downvotes ])

  useEffect(() => {
    if (status) setUpvoteStatus(status)
    else setUpvoteStatus(UpvoteStatus.none)
  }, [ status ])

  const onUpvoteStatusChange = (action: UpvoteStatus) => {
    setUpvoteStatus(action)
  }

  const onUpvote = async () => {
    onUpvoteClick()
    const response = await getSingleTip({
      variables: { tipDetailedId: id },
    })

    const channelState = isPublic ? PublicChannelState : PrivateChannelState

    upvote({
      variables: {
        id: id,
        type: contentType,
        cursor: channelState.prevOffset ? {
          id: channelState.prevOffset.id,
          createdAt: channelState.prevOffset.createdAt
        } : null,
        channelId: CurrentUserState.currentChannelId
      }
    })
      .catch((e) => {
        console.error(e)
        onDownvoteClick()
      })
    captureAnalytics(PosthogEvent.UPVOTE_TIP, {
      userId: CurrentUserState.id,
      sports: response.data.tipDetailed.sportId,
      market: response.data.tipDetailed.event?.tournament.externalId,
      league:response.data.tipDetailed.event?.tournament.name,
      is_single: !response.data.tipDetailed.isMultitip,
      tipster:response.data.tipDetailed.user.displayName
    })
  }

  const onDownvote = async() => {
    onDownvoteClick()

    const channelState = isPublic ? PublicChannelState : PrivateChannelState

    downvote({
      variables: {
        id: id,
        type: contentType,
        cursor: channelState.prevOffset ? {
          id: channelState.prevOffset.id,
          createdAt: channelState.prevOffset.createdAt
        } : null,
        channelId: CurrentUserState.currentChannelId
      }
    })
      .catch(
        (e) => {
          console.error(e)
          onUpvoteClick()
        }
      )
  }
  const onUpvoteClick = () => {
    switch (upvoteStatus) {
      case UpvoteStatus.upvote: {
        onUpvoteStatusChange(UpvoteStatus.none)
        setUpvotesCount(upvotesCount === null ? null : upvotesCount - 1)
        break
      }
      case UpvoteStatus.none: {
        onUpvoteStatusChange(UpvoteStatus.upvote)
        setUpvotesCount(upvotesCount === null ? null : upvotesCount + 1)
        break
      }
      case UpvoteStatus.downvote: {
        onUpvoteStatusChange(UpvoteStatus.upvote)
        setUpvotesCount(upvotesCount === null ? null : upvotesCount + 2)
        break
      }
    }
  }

  const onDownvoteClick = () => {
    switch (upvoteStatus) {
      case UpvoteStatus.upvote: {
        onUpvoteStatusChange(UpvoteStatus.downvote)
        setUpvotesCount(upvotesCount === null ? null : upvotesCount - 2)
        break
      }
      case UpvoteStatus.none: {
        onUpvoteStatusChange(UpvoteStatus.downvote)
        setUpvotesCount(upvotesCount === null ? null : upvotesCount - 1)
        break
      }
      case UpvoteStatus.downvote: {
        onUpvoteStatusChange(UpvoteStatus.none)
        setUpvotesCount(upvotesCount === null ? null : upvotesCount + 1)
        break
      }
    }
  }

  const getContentLink = (): string => {
    return `${window.location.origin.toString()}${
      ROUTES.content.short
    }/${id}?type=${contentType}`
  }

  const onCommentsClick = () => {
    isPublic
      ? (PublicChannelState.scrollPosition = window.scrollY)
      : (PrivateChannelState.scrollPosition = window.scrollY)

    navigate(`${ROUTES.content.short}/${id}?type=${contentType}`)
  }

  const onShareClick = async () => {
    const url = getContentLink()
    if (navigator.share) {
      await navigator.share({ url })
      return
    }
    await navigator.clipboard.writeText(url)
  }

  const propmtSignupModal = showSignupPromptModal({
    message: t('incognito.modal.loginDefaultMessage'),
    title: t('incognito.modal.loginTitle'),
  })


  return (
    <ActionsContainerWrapper>
      <ActionsContainer>
        <UpvoteSection>
          <VoteButton
            upvoted={upvoteStatus === UpvoteStatus.upvote}
            $isLightTheme={themeManager.isLight()}
            onClick={isGuest ? propmtSignupModal : onUpvote}
          >
            <Icon
              type={IconTypes.arrowFull}
              color={
                themeManager.isLight()
                  ? IconColor.lime600
                  : (upvoteStatus === UpvoteStatus.upvote ? IconColor.lime400 : IconColor.voteInactive)
              }
              width={12}
              height={12}
            />
          </VoteButton>
          <VotesCount count={upvotesCount}>{upvotesCount}</VotesCount>
          <VoteButton
            downvote
            upvoted={upvoteStatus === UpvoteStatus.downvote}
            $isLightTheme={themeManager.isLight()}
            onClick={isGuest ? propmtSignupModal : onDownvote}
            style={{ transform: 'rotate(180deg)' }}
          >
            <Icon
              type={IconTypes.arrowFull}
              color={
                themeManager.isLight()
                  ? IconColor.red600
                  : IconColor.red
              }
              width={12}
              height={12}
            />
          </VoteButton>
        </UpvoteSection>
        {commentsAllowed && (
          <CommentSection>
            <Button
              className="icon"
              onClick={isGuest ? propmtSignupModal : onCommentsClick}
            >
              <CommentsCount>{Number.isFinite(commentsCount) ? commentsCount : null}</CommentsCount>
              <Icon
                type={IconTypes.comments}
                color={IconColor.commentInactive}
                width={24}
                height={24}
              />
            </Button>
          </CommentSection>
        )}
        {!hideSave && (
          <StarButton type="button" onClick={isGuest ? propmtSignupModal : onSaveClick}>
            <Icon
              type={savedStatus ? IconTypes.starLineIconFilled : IconTypes.starLineIcon}
              color={savedStatus ? IconColor.quaternaryGolden : IconColor.starInactive}
              width={20}
              height={20}
            />
          </StarButton>
        )}
        <ShareButton type="button" onClick={onShareClick} style={hideSave ? { marginLeft: 'auto' } : undefined}>
          <Icon
            type={isTipContent ? IconTypes.shareArrowIcon : IconTypes.shareIconUpdate}
            color={IconColor.shareInactive}
            width={isTipContent ? 20 : 22}
            height={isTipContent ? 20 : 22}
          />
        </ShareButton>
      </ActionsContainer>
    </ActionsContainerWrapper>
  )
}

const ActionsContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ActionsContainer = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
  padding: 11px 16px 12px 16px;
  border-radius: 0 0 8px 8px;

  [data-theme="light"] & {
    background: var(--tip-card-header-gradient);
  }



  .icon {
    align-items: center;
    display: flex;
    height: 18px;
    background: transparent;
    padding: 0;
  }

  .vote-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    padding: 0;
    background: var(--bg-octonary);
    border: 0.5px solid transparent;
  }

  .vote-icon.upvoted {
    background: color-mix(in srgb, #A3E635 5%, var(--bg-octonary));
    border: 0.5px solid rgba(163, 230, 53, 0.4);
  }

  .downvote {
    transform: rotate(180deg);
  }
`

const UpvoteSection = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-right: 24px;
  padding: 3px;
  border-radius: 9px;
  background: var(--vote-group-bg);
  font-weight: 900;
`

const VotesCount = styled.p<{ count: number | null }>`
  font-weight: 500;
  color: ${({ count }) => count > 0 ? '#A3E635' : count < 0 ? '#FA7676' : 'inherit'};

  [data-theme="light"] & {
    color: ${({ count }) => count > 0 ? '#65A30D' : count < 0 ? '#DC2626' : 'inherit'};
  }
`

const VoteButton = styled.button<{ upvoted?: boolean; downvote?: boolean; $isLightTheme?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  padding: 0;
  cursor: pointer;
  background: ${({ upvoted, downvote, $isLightTheme }) => {
    if (!$isLightTheme) {
      return downvote
        ? 'color-mix(in srgb, #FF3B30 4%, rgba(24, 24, 27, 0.6))'
        : 'color-mix(in srgb, #A3E635 5%, rgba(24, 24, 27, 0.6))'
    }
    if (downvote) return `color-mix(in srgb, #DC2626 ${upvoted ? 16 : 6}%, var(--feed-actions-bg))`
    return `color-mix(in srgb, #65A30D ${upvoted ? 18 : 7}%, var(--feed-actions-bg))`
  }};
  border: 0.5px solid ${({ upvoted, downvote, $isLightTheme }) => {
    if (!$isLightTheme) {
      return downvote ? 'rgba(250, 118, 118, 0.4)' : 'rgba(163, 230, 53, 0.4)'
    }
    if (downvote) return upvoted ? 'rgba(220, 38, 38, 0.7)' : 'rgba(220, 38, 38, 0.35)'
    return upvoted ? 'rgba(101, 163, 13, 0.7)' : 'rgba(101, 163, 13, 0.35)'
  }};
`

const CommentsCount = styled.p`
  font-weight: 500;
  color: var(--text-senary);
`

const CommentSection = styled.div`
  display: flex;
  align-items: center;
  font-weight: 900;
`

const StarButton = styled.button`
  margin-left: auto;
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const ShareButton = styled.button`
  margin-left: 16px;
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

export default memo(FeedPostActions)